import React from 'react';
import api from 'controllers/raymanAPI';
import {CategoriesCtx, CategoriesCtxProvider} from './itemCategories';
import {ItemIconsCtx as IconsCtx, ItemIconsCtxProvider as IconsCtxProvider} from './itemIcons';
import Singletons from 'controllers/Singletons';

function map(item)
{
    return {...item, id: item.itemID}
}

async function getData()
{
    let indexed = {};

    let response = await api.procedures.items.getAll();
    let data = await response.json();
    data = data.map((item)=>map(item));
    data.forEach(item => {
        indexed[item.itemID] = item;
    });
    
    return {array: data, indexed: indexed};
}

async function getTypes()
{
    let indexed = {};

    let response = await api.procedures.items.types.getAll();
    let data = await response.json();
    data = data.map((item)=>map(item));
    data.forEach(item => {
        indexed[item.typeID] = item;
    });
    
    return {array: data, indexed: indexed};
}

const ProcedureItemsCtx = React.createContext({
    loading: true, 
    items: {
        array: [], 
        indexed: {}
    },
    itemTypes: {
        array: [],
        indexed: {}
    },
    icons: {
        array: [],
        indexed: {},
        funcs: {
            create: () => {},
            update: () => {},
            delete: () => {},
        }
    },
    categories: {
        array: [],
        indexed: {},
        funcs: {
            create: () => {},
            update: () => {},
            delete: () => {},
        }
    },
    funcs: {
        create: () => {},
        update: () => {},
        delete: () => {},
    }
});

function ProcedureItemsCtxProvider(props)
{

    const [loading, setLoading] = React.useState(true);
    const [items, setItems] = React.useState({array: [], indexed: []});
    const [types, setTypes] = React.useState({array: [], indexed: []});
    const iconsCtx = React.useContext(IconsCtx);
    const categoriesCtx = React.useContext(CategoriesCtx);

    React.useEffect(() => {
        let dataLoaded = false;
        let typesLoaded = false;

        getData().then(values => {
            setItems(values);
            dataLoaded = true;
            if(typesLoaded && dataLoaded) setLoading(false);
        });

        getTypes().then(values=> {
            setTypes(values);
            typesLoaded = true;
            if(typesLoaded && dataLoaded) setLoading(false);
        });

    }, []);

    const internalUpdate = React.useCallback((itemID, itemData) => {
        let tmp = {...items};
        Object.keys(itemData).forEach((key) => {
            tmp.indexed[itemID][key] = itemData[key];
        })
        setItems(tmp);
    }, [items]);

    const internalAdd = React.useCallback(async (itemID, item) => {
        if(items.indexed[itemID] !== undefined) return internalUpdate(itemID, item);

        let tmp = {...items};
        item = map(item);
        let index = tmp.array.push(item);
        tmp.indexed[item.id] = tmp.array[index-1];

        setItems(tmp);

    }, [items, internalUpdate]);

    const internalDelete = React.useCallback((itemID) => {
        let tmp = {...items};
        let item = tmp.indexed[itemID];

        // remove from array
        let index = tmp.array.indexOf(item);
        if(index > -1) tmp.array.splice(index,1);

        delete tmp.indexed[itemID];

        setItems(tmp);

    }, [items]);


    const create = React.useCallback(async (data) => {
        let result = await api.procedures.items.post(data);
        if(result.ok)
        {
            let item = await result.json();
            internalAdd(item.itemID, item);
        }
    }, [internalAdd]);

    const update = React.useCallback(async (itemID, data) => {
        let result = await api.procedures.items.updateSingle(itemID, data);
        if(result.ok) internalUpdate(itemID, data);

    }, [internalUpdate]);

    const remove = React.useCallback(async (itemID) => {
        let result = await api.procedures.items.deleteSingle(itemID);
        if(result.ok) internalDelete(itemID);
    }, [internalDelete]);

    React.useEffect( function handleNotifications() {

        const handleCreate = (message) => internalAdd(message.itemID, message);
        // const handleEnable = (message) => internalUpdate(message.itemID, {disabled: false});
        // const handleDisable = (message) => internalUpdate(message.itemID, {disabled: true});
        const handleUpdate = (message) => internalUpdate(message.itemID, message);
        const handleDelete = (message) => internalDelete(message.itemID, message);

        Singletons.NotificationsSocket.on('procedures.items.created', handleCreate);
        // Singletons.NotificationsSocket.on('procedures.items.disabled', handleDisable);
        // Singletons.NotificationsSocket.on('procedures.items.enabled', handleEnable);
        Singletons.NotificationsSocket.on('procedures.items.updated', handleUpdate);
        Singletons.NotificationsSocket.on('procedures.items.deleted', handleDelete);

        return () => {
            Singletons.NotificationsSocket.off('procedures.items.created', handleCreate);
            // Singletons.NotificationsSocket.off('procedures.items.disabled', handleDisable);
            // Singletons.NotificationsSocket.off('procedures.items.enabled', handleEnable);
            Singletons.NotificationsSocket.off('procedures.items.updated', handleUpdate);
            Singletons.NotificationsSocket.off('procedures.items.deleted', handleDelete);
        }

    }, [internalAdd, internalUpdate, internalDelete]);


    return <ProcedureItemsCtx.Provider value={{loading: loading, items: {...items}, itemTypes: {...types}, icons: iconsCtx, categories: categoriesCtx, funcs: {create: create, update: update, delete: remove}}} >
        {props.children}
        </ProcedureItemsCtx.Provider>
}


function ProcedureItemsCtxProviderWrapper(props)
{
    return <IconsCtxProvider><CategoriesCtxProvider><ProcedureItemsCtxProvider {...props} /></CategoriesCtxProvider></IconsCtxProvider>;
}

export {ProcedureItemsCtx, ProcedureItemsCtxProviderWrapper as ProcedureItemsCtxProvider};